/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {ForwardRefExoticComponent, RefAttributes, SVGProps} from 'react'
import {useTranslation} from 'react-i18next'
import {
	BellIcon,
	BuildingOfficeIcon,
	CalendarIcon,
	ChartBarIcon,
	ClipboardDocumentListIcon,
	Cog6ToothIcon,
	EnvelopeIcon,
	FolderIcon,
	LightBulbIcon,
	UserCircleIcon,
	UserIcon,
	UsersIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {useTypedSelector} from '../helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {ModalSize} from 'types/enums/ModalSize'
import SidebarItem from './SidebarComponents/SidebarItem'
import pkg from '../../package.json'

export interface NavItem {
    name: string
    icon: ForwardRefExoticComponent<
        Omit<SVGProps<SVGSVGElement>, 'ref'> & {
            title?: string
            titleId?: string
        } & RefAttributes<SVGSVGElement>
    >
    link: string
    position: number
    enabled: boolean
}

const Sidebar: React.FC = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const secondaryModal =
        useTypedSelector((state) => state.ui?.secondaryModal) ??
        {
            isOpen: false,
            props: {},
        }
    const isBlurred = secondaryModal.isOpen && secondaryModal.props?.modalSize !== ModalSize.RoundedFullscreen

    const navItems: NavItem[] = [
        {
            name: t('Accounts'),
            icon: BuildingOfficeIcon,
            link: '/companies',
            position: 5,
            enabled: true,
        },
        {
            name: t('Contacts'),
            icon: UsersIcon,
            link: '/contact_companies/list',
            position: 6,
            enabled: true,
        },
        { name: t('Leads'), icon: UserIcon, link: '/leads', position: 1, enabled: true },
        {
            name: t('Opportunities'),
            icon: LightBulbIcon,
            link: '/opportunities',
            position: 2,
            enabled: true,
        },
        { name: t('Offers'), icon: FolderIcon, link: '/offers', position: 3, enabled: true },
        {
            name: t('Email Templates'),
            icon: EnvelopeIcon,
            link: '/offer-email-templates',
            position: 9,
            enabled: true,
        },
        {
            name: t('Calendar'),
            icon: CalendarIcon,
            link: '/calendar',
            position: 8,
            enabled: true,
        },
        {
            name: t('Reservations'),
            icon: ClipboardDocumentListIcon,
            link: '/reservations',
            position: 4,
            enabled: true,
        },
        {
            name: t('Online Reservations'),
            icon: ClipboardDocumentListIcon,
            link: '/online-reservations',
            position: 10,
            enabled: true,
        },
        {
            name: t('Reminders'),
            icon: BellIcon,
            link: '/reminders',
            position: 7,
            enabled: true,
        },
        {
            name: t('Profile'),
            icon: UserCircleIcon,
            link: '/profile',
            position: 12,
            enabled: false,
        },
        {
            name: t('Reports'),
            icon: ChartBarIcon,
            link: '/reports',
            position: 10,
            enabled: false,
        },
        {
            name: t('Settings'),
            icon: Cog6ToothIcon,
            link: '/settings',
            position: 11,
            enabled: false,
        },
    ]

    return (
        <aside
            className={clsx(
                'Sidebar fixed top-0 left-0 h-full w-[var(--sidebar-max-width)] z-40 flex flex-col mt-14 select-none bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100',
                isBlurred && 'blur-sm transition-all duration-300'
            )}
        >
            <nav className={clsx('flex flex-col mt-2 px-1.5 space-y-0.5 overflow-y-auto custom-scrollbar pb-2')}>
                {navItems
                    .filter((item) => item.enabled)
                    .sort((a, b) => a.position - b.position)
                    .map((item) => (
                        <SidebarItem key={item.name} item={item} />
                    ))}
            </nav>

            {/* subtle project version, bottom-left */}
            <div className="grow px-3 py-3 relative flex flex-col items-center">
                <div className="absolute bottom-10 h-12 text-xs text-gray-500 dark:text-gray-400">v{pkg.version}</div>
            </div>
        </aside>
    )
}

export default Sidebar
